import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
export default function Profile() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [favs, setFavs] = useState([]);

  useEffect(() => {
    if (user === false) { navigate("/login?next=/profile"); return; }
    if (!user || !user.id) return;
    api.get("/listings", { params: { seller_id: user.id } })
      .then(({ data }) => setListings(data)).catch(() => {});
    api.get("/favorites").then(({ data }) => setFavs(data)).catch(() => {});
  }, [user, navigate]);

  const removeListing = async (id) => {
    if (!confirm(t("profile.delete_confirm"))) return;
    try {
      await api.delete(`/listings/${id}`);
      setListings((prev) => prev.filter((l) => l.id !== id));
      toast.success(t("profile.delete_success"));
    } catch { toast.error(t("profile.delete_error")); }
  };

  if (!user || !user.id) return null;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 fade-in">
      <div className="flex items-start gap-5">
        <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display text-2xl">
          {(user.name || "U").slice(0, 1)}
        </div>
        <div className="flex-1">
          <h1 className="font-display text-3xl">{user.name}</h1>
          <div className="text-sm text-muted-foreground">{user.email}</div>
          {user.bio && <p className="mt-2 text-sm text-muted-foreground max-w-xl">{user.bio}</p>}
        </div>
        <Button variant="outline" className="rounded-full" onClick={logout} data-testid="logout-btn">
          {t("profile.logout")}
        </Button>
      </div>

      <Tabs defaultValue="listings" className="mt-10">
        <TabsList className="rounded-full bg-muted/50 p-1">
          <TabsTrigger value="listings" className="rounded-full px-4" data-testid="tab-listings">
            {t("profile.tab_listings")} ({listings.length})
          </TabsTrigger>
          <TabsTrigger value="favorites" className="rounded-full px-4" data-testid="tab-favorites">
            {t("profile.tab_favorites")} ({favs.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="listings" className="mt-6">
          {listings.length === 0 ? (
            <EmptyState label={t("profile.empty_listings")} cta={t("profile.cta_sell")} to="/sell" />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
              {listings.map((it) => (
                <div key={it.id} className="relative group">
                  <ProductCard item={it} />
                  <button
                    onClick={() => removeListing(it.id)}
                    data-testid={`delete-listing-${it.id}`}
                    className="absolute top-2.5 left-2.5 opacity-0 group-hover:opacity-100 transition-opacity w-9 h-9 rounded-full bg-background/90 backdrop-blur flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="favorites" className="mt-6">
          {favs.length === 0 ? (
            <EmptyState label={t("profile.empty_favorites")} cta={t("profile.cta_browse")} to="/" />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
              {favs.map((it) => <ProductCard key={it.id} item={it} />)}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Invite friends (viral referral) */}
      <InviteFriends user={user} />
    </div>
  );
}

function InviteFriends({ user }) {
  const { t } = useTranslation();
  const [count, setCount] = useState(0);
  const code = user?.referral_code || (user?.id || "").slice(0, 8);
  const referral = `${window.location.origin}/?ref=${code}`;

  useEffect(() => {
    api.get("/referrals/count")
      .then(({ data }) => setCount(data.count || 0))
      .catch(() => {});
  }, []);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referral);
      toast.success(t("invite.copied"));
    } catch { toast.error("!"); }
  };
  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(`${t("invite.message_prefix")} ${referral}`)}`;

  return (
    <section className="mt-14 rounded-3xl border border-primary/20 bg-gradient-to-br from-secondary/70 to-background p-8 sm:p-10 relative overflow-hidden" data-testid="invite-section">
      <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
      <div className="relative max-w-2xl">
        <div className="flex items-center gap-3 mb-3">
          <div className="inline-flex items-center gap-2 px-3 h-7 rounded-full bg-primary/10 text-primary text-xs font-semibold" data-testid="referral-count">
            <span className="font-display text-sm leading-none">{count}</span>
            {t("invite.count_suffix")}
          </div>
        </div>
        <h2 className="font-display text-2xl sm:text-3xl leading-tight">
          {t("invite.title")}
        </h2>
        <p className="mt-3 text-sm text-muted-foreground">{t("invite.sub")}</p>
        <div className="mt-6 flex items-center gap-2 max-w-md rounded-full bg-background border border-border pl-4 pr-1 py-1">
          <input readOnly value={referral} data-testid="invite-input"
            className="flex-1 bg-transparent border-0 outline-none text-sm py-2 truncate" />
          <Button size="sm" onClick={copyLink} data-testid="invite-copy" className="rounded-full h-9 px-4">
            {t("invite.copy")}
          </Button>
        </div>
        <a
          href={whatsappHref} target="_blank" rel="noopener noreferrer"
          data-testid="invite-whatsapp"
          className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline underline-offset-4"
        >
          {t("invite.share_whatsapp")} →
        </a>
      </div>
    </section>
  );
}

const EmptyState = ({ label, cta, to }) => {
  const navigate = useNavigate();
  return (
    <div className="py-20 text-center">
      <div className="text-lg font-display">{label}</div>
      <Button className="mt-5 rounded-full" onClick={() => navigate(to)}>{cta}</Button>
    </div>
  );
};
